import { convertEventDateTimeToISO } from '@common/utils/timezone'
import moment from 'moment-timezone'
import { Calendar } from '@common/types/CalendarTypes'
import {
  RepetitionRule,
  VCalComponent,
  VObjectProperty,
  VObjectValue
} from '@common/features/Calendars/types/CalendarData'
import { userAttendee } from '@common/features/User/models/attendee'
import { CalendarEvent } from '@common/types/EventsTypes'
import { RepetitionObject } from '@common/types/Repetition'
import { buildDelegatedEventURL } from './buildDelegatedEventURL'
import { formatDateTimeToICal } from './formatDateToICal'
import { inferTimezoneFromValue } from './inferTimezoneFromValue'
import { WKST_NUM_TO_DAY } from './wkstUtils'
import { Attachment } from '@common/types/Attachment'
import { AlarmData, VAlarm } from '@common/types/VAlarm'
import { Valarms } from '@common/types/Valarms'
import { userOrganiser } from '@common/features/User/userDataTypes'

const KNOWN_PROPS = new Set([
  'uid',
  'transp',
  'dtstart',
  'dtend',
  'class',
  'x-openpaas-videoconference',
  'x-openpaas-booking-link',
  'summary',
  'description',
  'location',
  'organizer',
  'attendee',
  'dtstamp',
  'sequence',
  'recurrence-id',
  'exdate',
  'status',
  'duration',
  'rrule'
])

const DATE_REGEX = /^(\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])|\d{8})$/

function safeString(value: VObjectValue): string {
  if (value && typeof value === 'object') {
    if (value instanceof Date) {
      return value.toISOString()
    }
    return ''
  }
  return String(value)
}

interface PropertyContext {
  recurrenceId?: string
  duration?: string
}

function parseDateProperty(
  params: unknown,
  value: VObjectValue,
  event: Partial<CalendarEvent>,
  isStart: boolean
): void {
  const dateStr = safeString(value)
  if (isStart) {
    event.start = dateStr
  } else {
    event.end = dateStr
  }

  if (isStart || !event.timezone) {
    const detectedTz = inferTimezoneFromValue(params as Record<string, string>)
    if (detectedTz) {
      event.timezone = detectedTz
    }
  }

  event.allday = DATE_REGEX.test(dateStr)
}

function parseAttendeeProperty(
  params: unknown,
  value: VObjectValue,
  event: Partial<CalendarEvent>
): void {
  const paramsObj = params as Record<string, string>
  const calAddress = safeString(value).replace(/^mailto:/i, '')

  if (!event.attendee) {
    event.attendee = []
  }

  const alreadyExists = event.attendee.some(
    attendee => attendee.cal_address === calAddress
  )

  if (!alreadyExists) {
    event.attendee.push(
      new userAttendee({
        cn: paramsObj?.cn,
        cal_address: calAddress,
        partstat: paramsObj?.partstat as userAttendee['partstat'],
        rsvp: paramsObj?.rsvp as userAttendee['rsvp'],
        role: paramsObj?.role as userAttendee['role'],
        cutype: paramsObj?.cutype as userAttendee['cutype']
      })
    )
  }
}

function parseRruleProperty(
  value: VObjectValue,
  event: Partial<CalendarEvent>
): void {
  const ruleValue = value as RepetitionRule
  const repData: Partial<RepetitionObject> = {
    freq: ruleValue.freq.toLowerCase()
  }

  if (ruleValue.byday) {
    repData.byday =
      typeof ruleValue.byday === 'string' ? [ruleValue.byday] : ruleValue.byday
  }

  if (ruleValue.until) {
    repData.endDate = ruleValue.until
  }

  if (ruleValue.count) {
    repData.occurrences = ruleValue.count
  }

  if (ruleValue.interval) {
    repData.interval = ruleValue.interval
  }

  if (ruleValue.wkst != null) {
    repData.wkst =
      typeof ruleValue.wkst === 'number'
        ? (WKST_NUM_TO_DAY[ruleValue.wkst] ?? String(ruleValue.wkst))
        : ruleValue.wkst
  }

  event.repetition = new RepetitionObject(repData)
}

const PROPERTY_PARSERS: Record<
  string,
  (
    params: unknown,
    value: VObjectValue,
    event: Partial<CalendarEvent>,
    context: PropertyContext
  ) => void
> = {
  uid: (params, value, event) => {
    event.uid = safeString(value)
  },
  transp: (params, value, event) => {
    event.transp = safeString(value)
  },
  dtstart: (params, value, event) => {
    parseDateProperty(params, value, event, true)
  },
  dtend: (params, value, event) => {
    parseDateProperty(params, value, event, false)
  },
  class: (params, value, event) => {
    const classVal = safeString(value).toUpperCase()
    if (['PRIVATE', 'PUBLIC', 'CONFIDENTIAL'].includes(classVal)) {
      event.class = classVal as CalendarEvent['class']
    }
  },
  'x-openpaas-videoconference': (params, value, event) => {
    event.x_openpass_videoconference = safeString(value)
  },
  'x-openpaas-booking-link': (params, value, event) => {
    event.bookingLinkPublicId = safeString(value)
  },
  summary: (params, value, event) => {
    event.title = safeString(value)
  },
  description: (params, value, event) => {
    event.description = safeString(value)
  },
  location: (params, value, event) => {
    event.location = safeString(value)
  },
  organizer: (params, value, event) => {
    const paramsObj = params as Record<string, string>
    event.organizer = new userOrganiser({
      cn: paramsObj?.cn ?? '',
      cal_address: safeString(value).replace(/^mailto:/i, '')
    })
  },
  attendee: (params, value, event) => {
    parseAttendeeProperty(params, value, event)
  },
  dtstamp: (params, value, event) => {
    event.stamp = safeString(value)
  },
  sequence: (params, value, event) => {
    event.sequence = Number(value)
  },
  'recurrence-id': (params, value, event, context) => {
    context.recurrenceId = safeString(value)
  },
  exdate: (params, value, event) => {
    if (!event.exdates) event.exdates = []
    event.exdates.push(safeString(value))
  },
  status: (params, value, event) => {
    event.status = safeString(value)
  },
  duration: (params, value, event, context) => {
    context.duration = safeString(value)
  },
  rrule: (params, value, event) => {
    parseRruleProperty(value, event)
  },
  attach: (params, value, event) => {
    const paramsObj = (params ?? {}) as Record<string, string | undefined>
    if (!event.attach) event.attach = []
    event.attach.push(
      new Attachment(
        safeString(value),
        paramsObj.fmttype,
        paramsObj['x-filename']
      )
    )
  }
}

function parseEventProperty(
  prop: VObjectProperty,
  event: Partial<CalendarEvent>,
  context: PropertyContext
): void {
  const [key, params, , value] = prop
  const parser = PROPERTY_PARSERS[key.toLowerCase()]
  if (parser) {
    parser(params, value, event, context)
  }
}

function processEventUid(
  event: Partial<CalendarEvent>,
  recurrenceId?: string
): void {
  if (recurrenceId && event.uid) {
    event.uid = `${event.uid}/${recurrenceId}`
    event.recurrenceId = recurrenceId
  }
}

type AlarmPropertyHandler = (
  alarm: Partial<AlarmData>,
  value: VObjectValue
) => void

const ALARM_PROPERTY_HANDLERS: Record<string, AlarmPropertyHandler> = {
  action: (alarm, value) => {
    alarm.action = safeString(value)
  },
  trigger: (alarm, value) => {
    alarm.trigger = safeString(value)
  },
  description: (alarm, value) => {
    alarm.description = safeString(value)
  },
  summary: (alarm, value) => {
    alarm.summary = safeString(value)
  }
}

function parseAlarmAttendees(
  valarmProps: [string, unknown, unknown, unknown][]
): userAttendee[] {
  const attendees: userAttendee[] = []
  for (const [key, , , value] of valarmProps) {
    if (key.toLowerCase() === 'attendee') {
      const attendee = userAttendee.fromEmailField(
        safeString(value as VObjectValue)
      )
      if (attendee) attendees.push(attendee)
    }
  }
  return attendees
}

function parseSingleAlarm(
  valarm: [string, [string, unknown, unknown, unknown][], unknown[]]
): VAlarm {
  const alarm: Partial<AlarmData> = {}
  const valarmProps = valarm[1]

  for (const [key, , , value] of valarmProps) {
    const handler = ALARM_PROPERTY_HANDLERS[key.toLowerCase()]
    if (handler) handler(alarm, value as VObjectValue)
  }

  const attendees = parseAlarmAttendees(valarmProps)
  if (attendees.length > 0) alarm.attendees = attendees

  return new VAlarm(alarm as AlarmData)
}

function parseAlarms(valarms: unknown): Valarms | undefined {
  if (!Array.isArray(valarms) || !valarms.length) return undefined

  const alarms = (
    valarms as Array<[string, [string, unknown, unknown, unknown][], unknown[]]>
  ).map(parseSingleAlarm)

  return Valarms.fromList(alarms)
}

function processEventDates(
  event: Partial<CalendarEvent>,
  duration?: string
): void {
  const eventTimezone = event.timezone

  if (!event.end) {
    const start = event.start ? new Date(event.start) : new Date()
    const timeToAdd = duration
      ? moment.duration(duration).asMilliseconds()
      : moment.duration(30, 'minutes').asMilliseconds()
    const artificialEnd = new Date(start.getTime() + timeToAdd)
    event.end = formatDateTimeToICal(artificialEnd, eventTimezone)
  }

  if (!event.allday && eventTimezone) {
    if (event.start) {
      event.start =
        convertEventDateTimeToISO(event.start, eventTimezone) ?? event.start
    }

    if (event.end) {
      event.end =
        convertEventDateTimeToISO(event.end, eventTimezone) ?? event.end
    }
  }
}

export function parseCalendarEvent({
  data,
  color,
  calendar,
  eventURL,
  valarms
}: {
  data: VObjectProperty[]
  color: Record<string, string>
  calendar: Calendar
  eventURL: string
  valarms?: VCalComponent[]
}): CalendarEvent {
  const event: Partial<CalendarEvent> = { color, attendee: [] }
  const context: PropertyContext = {}

  for (const prop of data) {
    parseEventProperty(prop, event, context)
  }

  processEventUid(event, context.recurrenceId)

  const alarms = parseAlarms(valarms)
  if (alarms?.hasAlarms()) {
    event.alarms = alarms
  }

  event.calId = calendar.id
  event.URL = calendar.delegated
    ? buildDelegatedEventURL(calendar, eventURL)
    : eventURL

  if (!event.uid || !event.start) {
    console.error(
      `missing crucial event param in calendar ${calendar.id} `,
      data
    )
    event.error = `missing crucial event param in calendar ${calendar.id} `
  }

  processEventDates(event, context.duration)

  if (event.repetition) {
    event.repetition = RepetitionObject.fromFormValues(event.repetition, {
      allday: event.allday,
      timezone: event.timezone
    })
  }

  event.passthroughProps = data.filter(
    ([key]) => !KNOWN_PROPS.has(key.toLowerCase())
  )

  return event as CalendarEvent
}
